/**
 * Composable for querying ASHC controls for a policy or process domain.
 *
 * Thin wrapper over useCharModel. The policy -> control binding comes from the
 * framework registry (policyDomains[code].assuranceControls) and is surfaced by the
 * backend as `section.controls`; there is no table in this file to keep in step
 * with the framework. Retired EXC-* ids still resolve through each control's
 * legacy_ids so old deep links keep working.
 */

import type { Section } from '@/types/ashmatics';
import { useCharModel, type AshcControl } from '@/composables/useCharModel';

export type { AshcControl };

export function useControls() {
  const model = useCharModel();

  /**
   * Controls for an explorer section. Prefers the ids the backend attached
   * (from navigation.json, refreshed from the registry), then the registry
   * binding for the section's domain code.
   */
  const getControlsForSection = (section: Section | null | undefined): AshcControl[] => {
    if (!section) return [];
    if (section.controls?.length) return model.getControls(section.controls);
    return model.controlsForPolicy(section.domain_code);
  };

  const getControlsForPolicy = (code: string): AshcControl[] => model.controlsForPolicy(code);

  const getControlById = (id: string): AshcControl | undefined => model.getControl(id);

  /** Controls whose derived_from cites a given ISO 42001 Annex A clause (e.g. "A.5.2"). */
  const getControlsByIsoClause = (clause: string): AshcControl[] =>
    model.controls.filter((c) => (c.derived_from?.iso_42001_clauses ?? []).includes(clause));

  /** Controls bound to a process domain through char_bindings. */
  const getControlsForProcessDomain = (code: string): AshcControl[] =>
    model.controls.filter((c) => (c.char_bindings?.process_domains ?? []).includes(code));

  return {
    families: model.families,
    controls: model.controls,
    getControlsForSection,
    getControlsForPolicy,
    getControlById,
    getControlsByIsoClause,
    getControlsForProcessDomain,
    requirementsAddressedBy: model.requirementsAddressedBy,
    frameworks: model.frameworks,
  };
}
